/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  AutoAwesome as AiIcon,
  Send as SendIcon,
  Calculate as SimIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lightbulb as LightbulbIcon,
} from "@mui/icons-material";
import { PackageConfigState, PackagingConfig } from "../interfaces/packageConfig";
import { fetchData } from "../utils/dataProvider";
import { POST } from "../utils/constants";

interface ChatMessage {
  id: string;
  role: "assistant" | "user" | "error";
  text: string;
}

interface SimulationResult {
  bultos: number;
  totalWeight: string;
  savedPct: number;
}

interface GeminiChatRequest {
  userMessage: string;
  packageState: { config: PackagingConfig };
  simulationResult?: SimulationResult | null;
}

interface GeminiChatResponse {
  status: number;
  data: {
    response: string;
  };
}

const runSimulation = (
  state: PackageConfigState,
  qty: number,
  wPerItem: number,
): SimulationResult => {
  const rules = state.config.rules;
  const totalW = qty * wPerItem;

  const maxWeight = rules.max_weight_per_parcel || 10;
  const maxUnits = rules.max_units_per_parcel || 5;
  const pkgWeight = 0.3; // Peso promedio de embalaje

  const byWeight = Math.ceil(totalW / Math.max(maxWeight - pkgWeight, 0.01));
  const byUnits = Math.ceil(qty / Math.max(maxUnits, 1));
  const bultos = Math.max(byWeight, byUnits, 1);

  const totalWeight = (totalW + (bultos * pkgWeight)).toFixed(2);
  const saved = qty - bultos;
  const savedPct = qty > 1 ? Math.round((saved / qty) * 100) : 0;

  return { bultos, totalWeight, savedPct };
};

async function queryGeminiService(
  userMsg: string,
  state: PackageConfigState,
  simResult: SimulationResult | null,
): Promise<string> {
  try {
    const requestBody: GeminiChatRequest = {
      userMessage: userMsg,
      packageState: { config: state.config },
      simulationResult: simResult,
    };

    const { data } = (await fetchData<GeminiChatRequest>({
      url: "/gemini/chat",
      method: POST,
      body: requestBody,
    })) as GeminiChatResponse;

    return data.response;
  } catch (error: unknown) {
    // Manejar diferentes tipos de errores
    const err = error as {
      status?: number;
      code?: string;
      message?: string;
    };
    if (err.status === 401) {
      throw new Error("Sesión expirada. Por favor, iniciá sesión nuevamente.");
    }

    if (err.status === 400) {
      throw new Error("Datos de configuración inválidos.");
    }

    if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
      throw new Error(
        "El asesor está tardando mucho en responder. Intentá nuevamente.",
      );
    }

    throw new Error(
      err.message || "Error al consultar el asesor. Intentá nuevamente.",
    );
  }
}

const QUICK_QUESTIONS = [
  "¿Cómo optimizo mi configuración actual?",
  "¿Qué reglas me convienen para reducir costos?",
  "¿Cómo impactan mis cajas en las cotizaciones?",
  "Dame tips para mi catálogo de productos",
];

const INITIAL_AI_MESSAGE: ChatMessage = {
  id: "init",
  role: "assistant",
  text: "¡Hola! Soy tu asesor de optimización logística con IA. Veo que estás configurando tu empaque. Preguntame cómo optimizar tu configuración actual para reducir costos de envío.",
};

/* ─────────────────────────────────────────────────────────────
   Simulation Component
─────────────────────────────────────────────────────────────── */
const SimulationSection: React.FC<{
  state: PackageConfigState;
  onResult: (_result: SimulationResult | null) => void;
}> = ({ state, onResult }) => {
  const [qty, setQty] = useState(5);
  const [weight, setWeight] = useState(2);
  const [result, setResult] = useState<SimulationResult | null>(null);

  const handleSimulate = () => {
    const simResult = runSimulation(state, qty, weight);
    setResult(simResult);
    onResult(simResult);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <SimIcon sx={{ fontSize: 18, color: "primary.main" }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Simulá tu configuración
        </Typography>
      </Stack>

      <Stack direction="row" gap={2} mb={2}>
        <TextField
          label="Cantidad"
          type="number"
          size="small"
          value={qty}
          onChange={(e) => setQty(parseInt(e.target.value, 10) || 1)}
          inputProps={{ min: 1, max: 200 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">uds.</InputAdornment>,
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Peso promedio"
          type="number"
          size="small"
          value={weight}
          onChange={(e) => setWeight(parseFloat(e.target.value) || 0.01)}
          inputProps={{ min: 0.01, step: 0.01 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">kg</InputAdornment>,
          }}
          sx={{ flex: 1 }}
        />
      </Stack>

      <Button
        variant="contained"
        size="small"
        color="secondary"
        onClick={handleSimulate}
        startIcon={<SimIcon />}
        sx={{ textTransform: "none", borderRadius: 1.5, mb: 2 }}
      >
        Simular
      </Button>

      {result && (
        <Alert severity="success" sx={{ fontSize: "0.8rem" }}>
          <strong>{result.bultos} bulto(s)</strong> • Peso total:{" "}
          <strong>{result.totalWeight}kg</strong>
          {result.savedPct > 0 && (
            <span>
              {" "}
              • Ahorro: <strong>{result.savedPct}%</strong>
            </span>
          )}
        </Alert>
      )}
    </Paper>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main AI Advisor Panel
─────────────────────────────────────────────────────────────── */
const AiAdvisorPanel: React.FC<{ state: PackageConfigState }> = ({ state }) => {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_AI_MESSAGE]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);

  const hasValidConfig =
    state.config.virtual_boxes.filter((b) => b.active).length > 0;

  const handleSend = async (msg: string) => {
    if (!msg.trim() || loading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      text: msg,
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const reply = await queryGeminiService(msg, state, simResult);
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}r`, role: "assistant", text: reply },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}e`,
          role: "error",
          text: e instanceof Error ? e.message : "Error inesperado",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    handleSend(input);
    setInput("");
  };

  if (!hasValidConfig) {
    return null; // No mostrar hasta que haya al menos una caja configurada
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        mt: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "primary.50",
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AiIcon sx={{ fontSize: 20, color: "primary.main" }} />
        </Box>
        <Box flexGrow={1}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            🤖 Asesor IA de Optimización
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Consultá cómo optimizar tu configuración con inteligencia artificial
          </Typography>
        </Box>
        <Chip
          icon={<LightbulbIcon sx={{ fontSize: 14 }} />}
          label="Gemini 2.0"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 500, fontSize: "0.72rem" }}
        />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2.5 }}>
          {/* Simulation Section */}
          <SimulationSection state={state} onResult={setSimResult} />

          {/* Messages */}
          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              mb: 2,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "grey.25",
            }}
          >
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent:
                    msg.role === "user" ? "flex-end" : "flex-start",
                  mb: 1.5,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: "85%",
                    px: 2,
                    py: 1.5,
                    borderRadius:
                      msg.role === "user"
                        ? "16px 4px 16px 16px"
                        : "4px 16px 16px 16px",
                    bgcolor: (() => {
                      if (msg.role === "error") return "error.50";
                      if (msg.role === "user") return "primary.main";
                      return "background.paper";
                    })(),
                    color:
                      msg.role === "user"
                        ? "primary.contrastText"
                        : "text.primary",
                    border: msg.role === "user" ? "none" : "1px solid",
                    borderColor:
                      msg.role === "error" ? "error.light" : "divider",
                  }}
                >
                  <Typography variant="body2" lineHeight={1.5}>
                    {msg.text}
                  </Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box display="flex" justifyContent="flex-start" mb={1.5}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: "4px 16px 16px 16px",
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CircularProgress size={16} />
                </Box>
              </Box>
            )}
          </Box>

          {/* Quick questions */}
          <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
            {QUICK_QUESTIONS.map((q) => (
              <Chip
                key={q}
                label={q}
                size="small"
                variant="outlined"
                onClick={() => handleSend(q)}
                disabled={loading}
                sx={{
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  borderRadius: 99,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              />
            ))}
          </Stack>

          {/* Input */}
          <Stack direction="row" gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Preguntá sobre tu configuración..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!input.trim() || loading}
              sx={{
                minWidth: 44,
                borderRadius: 1.5,
                px: 2,
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AiAdvisorPanel;
